import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Panel,
  PanelType,
  PrimaryButton,
  TextField,
  Separator,
  MessageBar,
  MessageBarType,
} from 'office-ui-fabric-react';
import {
  BankInterestPanelReducer,
  initialState,
  actions,
} from './BankInterestPanelReducer';
import { getOptions as getContactOptions } from '../../../helpers/contactHelper';
import { getLabel } from '../../../helpers/labelHelper';
import { Grid, GridRow, Column, MoneyField } from '../../common';
import {
  YearPicker,
  ElectionCyclePicker,
  FECElectionCyclePicker,
  DatePicker,
  ContactPicker,
} from '../../Pickers';
import { getNextElectionYear } from '../../../selectors';
import { validate, createPayload } from './BankInterestPanelValidations';
import { useContactSearch } from '../../../hooks/useContactSearch';

const BankInterestPanel = ({
  isOpen,
  onDismiss,
  onSave,
  session,
  periodEndDate = '',
}) => {
  const nextElectionYear = useSelector(getNextElectionYear);
  const contactTypeOptions = getContactOptions(session);
  const [state, localDispatch] = useReducer(
    BankInterestPanelReducer,
    initialState,
  );

  const [onResolveSuggestions, onRenderSuggestionsItem] = useContactSearch(
    state.electionCycle,
    state.electionYear,
    null,
    'Contribution',
  );

  useEffect(() => {
    localDispatch({
      type: actions.SET_DEFAULT_ELECTION_YEAR,
      data: { nextElectionYear: +nextElectionYear },
    });
  }, [nextElectionYear]);

  useEffect(() => {
    if (!state.dateReceived) {
      localDispatch({
        type: actions.HANDLE_CHANGE,
        data: {
          fieldName: 'dateReceived',
          value: periodEndDate,
        },
      });
    }
  }, [periodEndDate]);

  const clearSelectedContact = () => {
    localDispatch({
      type: actions.CLEAR_SELECTED_CONTACT,
    });
  };

  const resetContactFields = () => {
    localDispatch({
      type: actions.RESET_SELECTED_CONTACT_INFO,
    });
  };

  const onCreateNewContact = () => {
    localDispatch({
      type: actions.HANDLE_CHANGE,
      data: {
        fieldName: 'usingNewContact',
        value: true,
      },
    });
  };

  const onItemSelected = contact => {
    localDispatch({ type: actions.ON_CONTACT_SELECTED, data: { contact } });
    return null;
  };

  const onChange = fieldName => (e, option) => {
    const value = option.key !== undefined ? option.key : option;
    localDispatch({
      type: actions.HANDLE_CHANGE,
      data: {
        fieldName,
        value,
      },
    });
  };

  const onChangeDateReceived = date => {
    localDispatch({
      type: actions.HANDLE_CHANGE,
      data: {
        fieldName: 'dateReceived',
        value: date,
      },
    });
  };

  const close = () => {
    localDispatch({
      type: actions.RESET_FORM,
    });
    onDismiss();
  };

  const save = () => {
    const errors = validate(state, session);
    localDispatch({
      type: actions.SET_FORM_ERRORS,
      data: { errors },
    });

    if (!Object.values(errors).some(e => e.length > 0)) {
      const payload = createPayload(state);
      onSave(payload);
      close();
    }
  };

  const onRenderFooterContent = () => {
    return <PrimaryButton text="Save" onClick={save} />;
  };

  const isSpecialOrOtherElection =
    session.isFederal() && ['Special', 'Other'].includes(state.electionCycle);

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      headerText="Add Interest"
      isFooterAtBottom
      onRenderFooterContent={onRenderFooterContent}
      type={PanelType.large}
    >
      <div className="BankInterestPanel">
        <Grid>
          <GridRow>
            <Column lg={4}>
              <YearPicker
                label="Election Year"
                value={state.electionYear}
                onChange={onChange('electionYear')}
                required
                errorMessage={state.errors.electionYear}
              />
            </Column>
            <Column lg={4}>
              {session.isFederal() ? (
                <FECElectionCyclePicker
                  value={state.electionCycle}
                  onChange={onChange('electionCycle')}
                  required
                  errorMessage={state.errors.electionCycle}
                />
              ) : (
                <ElectionCyclePicker
                  value={state.electionCycle}
                  label="Election"
                  onChange={onChange('electionCycle')}
                  required
                  errorMessage={state.errors.electionCycle}
                />
              )}
            </Column>
            {isSpecialOrOtherElection && (
              <Column lg={4}>
                <TextField
                  label="Other Election Type"
                  required
                  errorMessage={state.errors.otherElectionType}
                  value={state.otherElectionType}
                  onChange={onChange('otherElectionType')}
                />
              </Column>
            )}
          </GridRow>
          <Separator />
          <GridRow>
            <Column lg={4}>
              <MoneyField
                value={state.amount}
                onChange={onChange('amount')}
                label="Amount"
                required
                errorMessage={state.errors.amount}
              />
            </Column>
            <Column lg={4}>
              <DatePicker
                label="Date Received"
                value={state.dateReceived}
                onChange={onChangeDateReceived}
                required
                errorMessage={state.errors.dateReceived}
              />
            </Column>
          </GridRow>
          <GridRow>
            <Column lg={4}>
              <TextField
                label="Reference Id"
                value={state.paymentReferenceId}
                onChange={onChange('paymentReferenceId')}
              />
            </Column>
            <Column lg={4}>
              <TextField
                label="Description"
                value={state.description}
                onChange={onChange('description')}
              />
            </Column>
          </GridRow>
          <Separator />
          <GridRow>
            <Column lg={9}>
              {state.errors.selectedContact && (
                <MessageBar messageBarType={MessageBarType.error}>
                  {state.errors.selectedContact}
                </MessageBar>
              )}
              <ContactPicker
                session={session}
                selectedContact={state.selectedContact}
                clearSelectedContact={clearSelectedContact}
                onResolveSuggestions={onResolveSuggestions}
                onRenderSuggestionsItem={onRenderSuggestionsItem}
                onItemSelected={onItemSelected}
                onChangeContactField={onChange}
                resetContactFields={resetContactFields}
                onCreateNewContact={onCreateNewContact}
                cancelAddNew={resetContactFields}
                contactType={{
                  label: 'Contact Type',
                  field: 'contactType',
                  required: true,
                  show: true,
                  errorMessage: state.errors.contactType,
                  value: state.contactType,
                  options: contactTypeOptions,
                }}
                committeeFecId={{
                  label: 'FEC ID',
                  field: 'committeeFecId',
                  value: state.committeeFecId,
                  required: true,
                  show: true,
                }}
                salutation={{
                  label: 'Salutation',
                  field: 'salutation',
                  value: state.salutation,
                  required: false,
                  show: true,
                }}
                relationshipToCandidate={{
                  label: 'Relationship To Candidate',
                  field: 'relationshipToCandidate',
                  value: state.relationshipToCandidate,
                  required: false,
                  show: true,
                }}
                firstName={{
                  label: 'First Name',
                  field: 'firstName',
                  value: state.firstName,
                  required: true,
                  show: true,
                  errorMessage: state.errors.firstName,
                }}
                middleName={{
                  label: 'Middle Name',
                  field: 'middleName',
                  value: state.middleName,
                  required: false,
                  show: true,
                }}
                lastName={{
                  label: 'Last Name',
                  field: 'lastName',
                  value: state.lastName,
                  required: true,
                  show: true,
                  errorMessage: state.errors.lastName,
                }}
                occupation={{
                  label: 'Occupation',
                  field: 'occupation',
                  value: state.occupation,
                  required: false,
                  show: true,
                }}
                employer={{
                  label: 'Employer',
                  field: 'employer',
                  value: state.employer,
                  required: false,
                  show: true,
                }}
                address1={{
                  label: getLabel('Address Line 1', session),
                  field: 'addressLine1',
                  value: state.addressLine1,
                  required: false,
                  show: true,
                }}
                address2={{
                  label: getLabel('Address Line 2', session),
                  field: 'addressLine2',
                  value: state.addressLine2,
                  required: false,
                  show: true,
                }}
                city={{
                  label: 'City',
                  field: 'city',
                  value: state.city,
                  required: false,
                  show: true,
                }}
                state={{
                  label: 'State',
                  field: 'state',
                  value: state.state,
                  required: false,
                  show: true,
                }}
                zipCode={{
                  label: 'Zip Code',
                  field: 'zipCode',
                  value: state.zipCode,
                  required: false,
                  show: true,
                }}
                county={{
                  label: 'County',
                  field: 'county',
                  value: state.county,
                  required: false,
                  show: true,
                }}
                phone1={{
                  label: 'Phone 1',
                  field: 'phone1',
                  value: state.phone1,
                  show: true,
                }}
                phone2={{
                  label: 'Phone 2',
                  field: 'phone2',
                  value: state.phone2,
                  show: true,
                }}
                email={{
                  label: 'Email Address',
                  field: 'email',
                  value: state.email,
                  show: true,
                  errorMessage: state.errors.email,
                }}
                businessName={{
                  label: 'Business Name',
                  field: 'businessName',
                  value: state.businessName,
                  required: true,
                  show: true,
                  errorMessage: state.errors.businessName,
                }}
                businessType={{
                  label: 'Business Type',
                  field: 'businessType',
                  value: state.businessType,
                  show: true,
                }}
                contactName={{
                  label: 'Contact Name',
                  field: 'contactName',
                  value: state.contactName,
                  show: true,
                }}
                committeeAffiliation={{
                  label: 'Committee Type',
                  field: 'committeeAffiliation',
                  value: state.committeeAffiliation,
                  show: true,
                }}
                commonSource={{
                  label: 'Common Source',
                  field: 'commonSource',
                  value: state.commonSource,
                  required: false,
                  show: true,
                }}
                specific={{
                  label: 'Specific',
                  field: 'specific',
                  value: state.specific,
                  required: false,
                  show: true,
                }}
              />
            </Column>
          </GridRow>
        </Grid>
      </div>
    </Panel>
  );
};

BankInterestPanel.propTypes = {
  isOpen: PropTypes.bool,
  onDismiss: PropTypes.func,
  onSave: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired,
  periodEndDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),
};

export default BankInterestPanel;
